import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, SlidersHorizontal } from "lucide-react";
import {
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { PAYMENT_TERMS } from "@/features/clients/constants";
import { useStockLocations } from "@/features/locations/api";
import { useProcessTypes } from "@/features/machines/api";
import { fiscalYearLabel } from "@/features/purchase/constants";
import {
  companyToDefaultsForm,
  useCompany,
  useCreateCompany,
  useUpdateDefaults,
} from "@/features/settings/api";
import { MONTHS, previewDocNumber } from "@/features/settings/constants";
import {
  defaultsFormSchema,
  emptyCompanyForm,
  emptyDefaultsForm,
  type DefaultsFormValues,
} from "@/features/settings/schema";

export function DefaultsSettingsPage() {
  return (
    <ErrorBoundary title="Defaults settings crashed">
      <DefaultsSettingsInner />
    </ErrorBoundary>
  );
}

function DefaultsSettingsInner() {
  const { data: company, isLoading } = useCompany();
  const { data: locations = [] } = useStockLocations();
  const { data: processTypes = [] } = useProcessTypes();
  const updateDefaults = useUpdateDefaults();
  const createCompany = useCreateCompany();

  const form = useForm<DefaultsFormValues>({
    resolver: zodResolver(defaultsFormSchema),
    defaultValues: emptyDefaultsForm,
    mode: "onBlur",
  });

  const { errors, isDirty } = form.formState;
  const fyMonth = form.watch("fiscal_year_start_month") || 4;

  useEffect(() => {
    if (company) form.reset(companyToDefaultsForm(company));
  }, [company, form]);

  const submit = form.handleSubmit(async (values) => {
    if (!company) {
      await createCompany.mutateAsync(emptyCompanyForm);
      return;
    }
    await updateDefaults.mutateAsync({ id: company.id, values });
  });

  const save = useCallback(() => {
    void submit();
  }, [submit]);

  const saving = updateDefaults.isPending || createCompany.isPending;
  useDocumentSaveShortcut(save, !saving && Boolean(company) && isDirty);

  if (isLoading) {
    return <p className="py-12 text-center text-sm text-muted-foreground">Loading defaults…</p>;
  }

  if (!company) {
    return (
      <DocumentPage className="max-w-3xl">
        <DocumentHeader
          title="Defaults"
          subtitle="Create a company profile first (Company tab)"
        />
        <Button
          type="button"
          size="sm"
          onClick={() => void createCompany.mutateAsync(emptyCompanyForm)}
        >
          Create company row
        </Button>
      </DocumentPage>
    );
  }

  const activeLocations = locations.filter((l) => l.is_active);

  return (
    <DocumentPage className="max-w-3xl pb-16">
      <DocumentHeader
        title="Defaults"
        subtitle="Applied when party or line has no value · Ctrl+S"
        actions={
          <Button type="button" size="sm" disabled={saving || !isDirty} onClick={save}>
            {saving ? <Loader2 className="animate-spin" /> : null}
            Save
          </Button>
        }
      />

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <FormSection
          id="ops"
          title="Operating defaults"
          variant="plain"
          icon={<SlidersHorizontal />}
        >
          <FieldGrid>
            <Field label="Payment terms" span={6} htmlFor="def-terms">
              <NativeSelect id="def-terms" {...form.register("default_payment_terms")}>
                <option value="">— None —</option>
                {PAYMENT_TERMS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              label="GST slab %"
              span={3}
              error={errors.default_gst_slab?.message}
              htmlFor="def-gst"
            >
              <Input
                id="def-gst"
                type="number"
                step="0.01"
                min={0}
                max={100}
                {...form.register("default_gst_slab")}
              />
            </Field>
            <Field
              label="FY starts"
              span={3}
              error={errors.fiscal_year_start_month?.message}
              htmlFor="def-fy"
              hint={`Now ${fiscalYearLabel(new Date(), fyMonth)} · e.g. ${previewDocNumber("INV", fyMonth)}`}
            >
              <NativeSelect
                id="def-fy"
                {...form.register("fiscal_year_start_month", { valueAsNumber: true })}
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Default stock location" span={12} htmlFor="def-loc">
              <NativeSelect id="def-loc" {...form.register("default_location_id")}>
                <option value="">— None —</option>
                {activeLocations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code} — {l.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection id="mill" title="Mill planning constants" variant="plain">
          <FieldGrid>
            <Field label="Kerf mm" span={3} htmlFor="mill-kerf">
              <Input id="mill-kerf" type="number" step="0.1" {...form.register("mill_kerf_mm")} />
            </Field>
            <Field label="Edge trim mm" span={3} htmlFor="mill-edge">
              <Input
                id="mill-edge"
                type="number"
                step="0.1"
                {...form.register("mill_edge_trim_mm")}
              />
            </Field>
            <Field label="Weld allow mm" span={3} htmlFor="mill-weld">
              <Input
                id="mill-weld"
                type="number"
                step="0.1"
                {...form.register("mill_weld_allowance_mm")}
              />
            </Field>
            <Field label="CTL blade mm" span={3} htmlFor="mill-blade">
              <Input
                id="mill-blade"
                type="number"
                step="0.1"
                {...form.register("mill_ctl_blade_mm")}
              />
            </Field>
            <Field label="CTL yield %" span={3} htmlFor="mill-yield">
              <Input
                id="mill-yield"
                type="number"
                step="0.1"
                {...form.register("mill_ctl_yield_margin_pct")}
              />
            </Field>
            <Field label="Skinpass factor" span={3} htmlFor="mill-skin">
              <Input
                id="mill-skin"
                type="number"
                step="0.0001"
                {...form.register("mill_skinpass_weight_factor")}
              />
            </Field>
            <Field label="Tube wt factor" span={3} htmlFor="mill-tube">
              <Input
                id="mill-tube"
                type="number"
                step="0.0000001"
                {...form.register("mill_tube_weight_factor")}
              />
            </Field>
            <Field label="Inbound default process" span={3} htmlFor="mill-inb">
              <NativeSelect
                id="mill-inb"
                {...form.register("mill_inbound_default_process_type_id")}
              >
                <option value="">— PICKLING —</option>
                {processTypes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </FieldGrid>
        </FormSection>
      </form>

      <DocumentStickyBar>
        <span className="text-2xs text-muted-foreground">
          {isDirty ? "Unsaved changes" : "All changes saved"}
        </span>
        <Button type="button" size="sm" disabled={saving || !isDirty} onClick={save}>
          {saving ? <Loader2 className="animate-spin" /> : null}
          Save defaults
        </Button>
      </DocumentStickyBar>
    </DocumentPage>
  );
}
